import { useState } from "react";
import { Bell, BellOff, Check, CheckCheck, X, Clock, Sparkles, Mail, MailOpen, ExternalLink, Trash2 } from "lucide-react";
import { useNotifications } from "../../contexts/NotificationContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import { formatDistanceToNow } from "date-fns";

const NotificationBell = ({ triggerClassName = "" }) => {
  const {
    notifications,
    unreadCount,
    permission,
    loading,
    requestPermission,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);

  const handleEnableNotifications = async () => {
    await requestPermission();
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // Navigate if there's a URL
    if (notification.data?.url) {
      window.location.href = notification.data.url;
      setIsOpen(false);
    }
  };

  const getNotificationTypeColor = (type = "") => {
    switch (type?.toLowerCase()) {
      case "order":
        return "bg-blue-500";
      case "production":
        return "bg-orange-500";
      case "quality":
        return "bg-green-500";
      case "dispatch":
        return "bg-purple-500";
      case "alert":
        return "bg-red-500";
      case "approval":
        return "bg-amber-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className={`relative h-8 w-8 p-0 rounded-full ${triggerClassName}`}>
          <Bell className="h-[18px] w-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <Badge
                variant="destructive"
                className="relative h-4 w-4 flex items-center justify-center p-0 text-[10px] rounded-full ring-2 ring-background"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </Badge>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-1rem)] sm:w-96 max-w-[400px] p-0 mx-2 sm:mx-0"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-2">
            {unreadCount > 0 ? (
              <Mail className="h-4 w-4 text-primary" />
            ) : (
              <MailOpen className="h-4 w-4 text-muted-foreground" />
            )}
            <div>
              <h4 className="font-semibold text-sm sm:text-base">Notifications</h4>
              {unreadCount > 0 && (
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {unreadCount} new
                </p>
              )}
            </div>
          </div>
          {notifications.length > 0 && unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-[10px] sm:text-xs h-7 px-2 gap-1"
            >
              <CheckCheck className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Mark all read</span>
              <span className="sm:hidden">Read all</span>
            </Button>
          )}
        </div>

        {/* Enable notifications prompt */}
        {permission === "default" && (
          <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-900/10 border-b">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  Enable push notifications to stay updated
                </p>
                <Button
                  size="sm"
                  onClick={handleEnableNotifications}
                  disabled={loading}
                  className="mt-2 h-7 sm:h-8 text-xs w-full sm:w-auto"
                >
                  {loading ? "Enabling..." : "Enable Now"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Blocked notifications warning */}
        {permission === "denied" && (
          <div className="p-3 sm:p-4 bg-gradient-to-r from-red-50 to-red-50/50 dark:from-red-900/20 dark:to-red-900/10 border-b">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <BellOff className="h-4 w-4 flex-shrink-0" />
              <div>
                <span className="text-xs sm:text-sm font-medium">Notifications blocked</span>
                <p className="text-[10px] sm:text-xs text-red-500/80 dark:text-red-400/70 mt-0.5">
                  Enable in browser settings
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notifications list */}
        <ScrollArea className="h-[50vh] sm:h-[320px] max-h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-6 sm:p-8 text-center">
              <div className="relative inline-block mb-3">
                <div className="p-3 sm:p-4 rounded-full bg-muted/50">
                  <Bell className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground/50" />
                </div>
              </div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">No notifications yet</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground/70 mt-1">
                You're all caught up!
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`group relative p-2.5 sm:p-3 cursor-pointer transition-all duration-200 ${
                    !notification.read
                      ? "bg-gradient-to-r from-primary/5 via-primary/3 to-transparent hover:from-primary/10 hover:via-primary/5"
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {/* Unread indicator */}
                  {!notification.read && (
                    <div className="absolute left-0 top-2 bottom-2 w-0.5 sm:w-1 rounded-full bg-primary" />
                  )}

                  <div className="flex items-start gap-2 sm:gap-3 pl-1.5 sm:pl-2">
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        {!notification.read && (
                          <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                        <p className={`text-xs sm:text-sm font-medium truncate ${
                          !notification.read
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }`}>
                          {notification.title}
                        </p>
                      </div>

                      {notification.body && (
                        <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 line-clamp-2 pr-2">
                          {notification.body}
                        </p>
                      )}

                      {/* Meta info */}
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2 flex-wrap">
                        {notification.type && (
                          <span className={`inline-flex items-center text-[8px] sm:text-[10px] px-1.5 py-0.5 rounded-full ${getNotificationTypeColor(notification.type)} text-white`}>
                            {notification.type}
                          </span>
                        )}
                        <span className="flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs text-muted-foreground">
                          <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          {notification?.created_at
                            ? formatDistanceToNow(new Date(notification.created_at), {
                                addSuffix: true,
                              })
                            : "just now"}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 sm:h-7 sm:w-7 hover:bg-green-100 hover:text-green-600 dark:hover:bg-green-900/30"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          title="Mark as read"
                        >
                          <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 sm:h-7 sm:w-7 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        title="Delete"
                      >
                        <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t bg-muted/30 space-y-1">
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 text-[10px] sm:text-xs h-8 gap-1.5 hover:bg-primary/10 hover:text-primary"
                onClick={() => {
                  window.location.href = "/notifications";
                  setIsOpen(false);
                }}
              >
                <span>View all</span>
                <ExternalLink className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 text-[10px] sm:text-xs h-8 gap-1.5 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                onClick={() => {
                  clearAllNotifications();
                  setIsOpen(false);
                }}
              >
                <Trash2 className="h-3 w-3" />
                <span>Clear all</span>
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
